/**
 * Keyboard navigation for the symbol picker's cascader popup, driven from the popup header's search
 * field (the only element that holds focus while the popup is open).
 *
 * Why not rc-cascader's own keyboard support: it lives on antd's trigger span, which the header
 * field never lets have focus, and it cannot be driven from outside — rc-cascader 1.4.3 keeps
 * `activeValue` in private state with no controlled prop. Worse, in search mode antd rebuilds every
 * flattened result (and its array `value`) on each render, so rc's identity-based cursor loses its
 * row the moment anything re-renders.
 *
 * So the cursor lives in the DOM: one row carries PICKER_CURSOR_ATTRIBUTE, and every move is read
 * back from the rendered rows, which makes it self-healing when antd re-renders. A branch is opened
 * and a leaf is picked by `row.click()` — literally the same rc-cascader handler a mouse click runs,
 * so Enter produces the same `onChange(value, selectedOptions)` and therefore the same ISymbol.
 *
 * Attributes rather than classes: antd rewrites the row and popup `className` on every render.
 */

/** Marks the keyboard cursor's row. */
export const PICKER_CURSOR_ATTRIBUTE: string = "data-picker-cursor";

/**
 * Present on the popup while the keyboard drives it. styling.scss takes the rows out of hit-testing
 * under it — exactly like the wheel guard — so a pointer resting over the panel neither paints a
 * second highlighted row nor hover-expands a branch while rows scroll under it. The first real
 * pointer movement removes it (see `attachPickerPopupBehaviours`).
 */
export const KEYBOARD_NAV_ATTRIBUTE: string = "data-keyboard-nav";

export type TPickerNavigationKey = "ArrowUp" | "ArrowDown" | "ArrowLeft" | "ArrowRight" | "Enter";

const NAVIGATION_KEYS: readonly string[] = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Enter"];

export function isPickerNavigationKey(key: string): key is TPickerNavigationKey {
  return NAVIGATION_KEYS.includes(key);
}

export interface IPickerNavigationOptions {
  /**
   * A search is running. The popup is then antd's single flat result list, and Left/Right are left
   * to the field so they move the caret through the typed text.
   */
  isSearching: boolean;
  /** Put focus back in the search field: rc-cascader focuses its own trigger on every row click. */
  restoreFocus: () => void;
  /**
   * Run `work` once React has committed. A row click dispatched from inside a React key handler is
   * batched, so the column it opens is not in the DOM until the handler has returned.
   */
  afterCommit: (work: () => void) => void;
}

interface IRowLocation {
  columnIndex: number;
  rowIndex: number;
}

function getColumns(popup: HTMLElement): HTMLElement[] {
  return Array.from(popup.querySelectorAll<HTMLElement>("ul.ant-cascader-menu"));
}

function getRows(column: HTMLElement): HTMLElement[] {
  return Array.from(column.querySelectorAll<HTMLElement>(":scope > li.ant-cascader-menu-item"));
}

function isSelectable(row: HTMLElement): boolean {
  return !row.classList.contains("ant-cascader-menu-item-disabled");
}

function isBranch(row: HTMLElement): boolean {
  return row.classList.contains("ant-cascader-menu-item-expand");
}

/** antd's `-active` row: the open branch of its column (and, for a leaf, never set by hover). */
function isOpen(row: HTMLElement): boolean {
  return row.classList.contains("ant-cascader-menu-item-active");
}

function locate(popup: HTMLElement, row: HTMLElement): IRowLocation | null {
  const columns: HTMLElement[] = getColumns(popup);
  for (let columnIndex = 0; columnIndex < columns.length; columnIndex++) {
    const rowIndex: number = getRows(columns[columnIndex]).indexOf(row);
    if (rowIndex !== -1) {
      return { columnIndex, rowIndex };
    }
  }
  return null;
}

function openRowIn(column: HTMLElement | undefined): HTMLElement | null {
  return column ? (getRows(column).find(isOpen) ?? null) : null;
}

/**
 * The next selectable row from `from` in `step` direction, wrapping at both ends. `from` of -1 means
 * "no row yet": the first selectable row going down, the last going up.
 */
function nextSelectable(rows: HTMLElement[], from: number, step: 1 | -1): HTMLElement | null {
  const count: number = rows.length;
  if (count === 0) return null;
  let start: number;
  if (from < 0) {
    start = step === 1 ? 0 : count - 1;
  } else {
    start = (((from + step) % count) + count) % count;
  }
  for (let offset = 0; offset < count; offset++) {
    const row: HTMLElement = rows[(((start + step * offset) % count) + count) % count];
    if (isSelectable(row)) return row;
  }
  return null;
}

export function getPickerCursor(popup: HTMLElement): HTMLElement | null {
  return popup.querySelector<HTMLElement>(`[${PICKER_CURSOR_ATTRIBUTE}]`);
}

/** Move the cursor to `row`, or clear it (and leave keyboard mode) with `null`. */
export function setPickerCursor(popup: HTMLElement, row: HTMLElement | null): void {
  popup.querySelectorAll<HTMLElement>(`[${PICKER_CURSOR_ATTRIBUTE}]`).forEach((marked: HTMLElement): void => {
    if (marked !== row) marked.removeAttribute(PICKER_CURSOR_ATTRIBUTE);
  });
  if (!row) {
    popup.removeAttribute(KEYBOARD_NAV_ATTRIBUTE);
    return;
  }
  row.setAttribute(PICKER_CURSOR_ATTRIBUTE, "");
  popup.setAttribute(KEYBOARD_NAV_ATTRIBUTE, "");
  // Optional: jsdom does not implement it.
  if (typeof row.scrollIntoView === "function") {
    row.scrollIntoView({ block: "nearest", inline: "nearest" });
  }
}

export function clearPickerCursor(popup: HTMLElement): void {
  setPickerCursor(popup, null);
}

/** Where navigation continues from: the cursor, else the deepest branch the pointer left open. */
function currentRow(popup: HTMLElement): HTMLElement | null {
  const cursor: HTMLElement | null = getPickerCursor(popup);
  if (cursor) return cursor;
  const columns: HTMLElement[] = getColumns(popup);
  for (let columnIndex = columns.length - 1; columnIndex >= 0; columnIndex--) {
    const open: HTMLElement | null = openRowIn(columns[columnIndex]);
    if (open) return open;
  }
  return null;
}

/** Open or collapse through the row's own click handler, then take focus back from rc's trigger. */
function clickRow(row: HTMLElement, options: IPickerNavigationOptions): void {
  row.click();
  options.restoreFocus();
}

/**
 * Pick a leaf through the row's own click handler — and leave focus where rc-cascader puts it (its
 * trigger), exactly as a mouse pick does. The popup is closing: pulling focus into its field would
 * strand it on `<body>` once the popup hides, and an enclosing dialog would stop hearing Escape/Tab.
 */
function pickLeaf(row: HTMLElement): void {
  row.click();
}

/**
 * Put the cursor on `row`. A branch opens as the cursor lands on it — the Finder behaviour the
 * pointer already has through hover-expand — so its children are on screen before Right is pressed.
 * A leaf collapses whatever column a previously open sibling left behind: re-selecting the parent's
 * row trims rc-cascader's active path back to this column.
 */
function arriveAt(popup: HTMLElement, row: HTMLElement, options: IPickerNavigationOptions): void {
  setPickerCursor(popup, row);
  if (isBranch(row)) {
    if (!isOpen(row)) clickRow(row, options);
    return;
  }
  const location: IRowLocation | null = locate(popup, row);
  const columns: HTMLElement[] = getColumns(popup);
  if (!location || location.columnIndex === 0 || columns.length <= location.columnIndex + 1) return;
  const parent: HTMLElement | null = openRowIn(columns[location.columnIndex - 1]);
  if (parent) clickRow(parent, options);
}

function arriveAtFirstRow(popup: HTMLElement, options: IPickerNavigationOptions): boolean {
  const first: HTMLElement | undefined = getColumns(popup)[0];
  const row: HTMLElement | null = first ? nextSelectable(getRows(first), -1, 1) : null;
  if (row) arriveAt(popup, row, options);
  return true;
}

/** Open `branch` (if it is not already) and move the cursor to the first row of its column. */
function enterBranch(popup: HTMLElement, branch: HTMLElement, options: IPickerNavigationOptions): void {
  const location: IRowLocation | null = locate(popup, branch);
  if (!location) return;
  const moveIn = (): void => {
    const child: HTMLElement | undefined = getColumns(popup)[location.columnIndex + 1];
    const first: HTMLElement | null = child ? nextSelectable(getRows(child), -1, 1) : null;
    if (first) arriveAt(popup, first, options);
  };
  if (isOpen(branch) && getColumns(popup).length > location.columnIndex + 1) {
    moveIn();
    return;
  }
  setPickerCursor(popup, branch);
  clickRow(branch, options);
  options.afterCommit(moveIn);
}

function moveVertically(popup: HTMLElement, step: 1 | -1, options: IPickerNavigationOptions): boolean {
  const current: HTMLElement | null = currentRow(popup);
  const location: IRowLocation | null = current ? locate(popup, current) : null;
  const columns: HTMLElement[] = getColumns(popup);
  const column: HTMLElement | undefined = columns[location ? location.columnIndex : 0];
  if (!column) return true;
  const target: HTMLElement | null = nextSelectable(getRows(column), location ? location.rowIndex : -1, step);
  if (target) arriveAt(popup, target, options);
  return true;
}

function moveLeft(popup: HTMLElement): boolean {
  const current: HTMLElement | null = currentRow(popup);
  const location: IRowLocation | null = current ? locate(popup, current) : null;
  if (!location || location.columnIndex === 0) return true;
  // The parent row is already open (it is what shows this column), so no click: the column the
  // cursor leaves stays visible, as it does in Finder.
  const parent: HTMLElement | null = openRowIn(getColumns(popup)[location.columnIndex - 1]);
  if (parent) setPickerCursor(popup, parent);
  return true;
}

function moveRight(popup: HTMLElement, options: IPickerNavigationOptions): boolean {
  const current: HTMLElement | null = currentRow(popup);
  if (!current) return arriveAtFirstRow(popup, options);
  if (isBranch(current)) enterBranch(popup, current, options);
  return true;
}

/**
 * Enter picks the cursor's leaf through its own click handler; on a branch it steps in like Right.
 * With no cursor during a search it picks the FIRST result — the top-ranked hit — so "type, Enter"
 * works; with no cursor while browsing it only places the cursor.
 */
function commit(popup: HTMLElement, options: IPickerNavigationOptions): boolean {
  const current: HTMLElement | null = currentRow(popup);
  if (!current) {
    if (!options.isSearching) return arriveAtFirstRow(popup, options);
    const results: HTMLElement | undefined = getColumns(popup)[0];
    const first: HTMLElement | null = results ? nextSelectable(getRows(results), -1, 1) : null;
    if (first && !isBranch(first)) pickLeaf(first);
    return true;
  }
  if (isBranch(current)) {
    enterBranch(popup, current, options);
    return true;
  }
  if (isSelectable(current)) pickLeaf(current);
  return true;
}

/**
 * Apply one navigation key to `popup`. Returns whether the key was consumed — the caller then
 * prevents its default (the caret jumping to the start or end of the field).
 */
export function handlePickerNavigationKey(
  popup: HTMLElement,
  key: TPickerNavigationKey,
  options: IPickerNavigationOptions
): boolean {
  switch (key) {
    case "ArrowDown":
      return moveVertically(popup, 1, options);
    case "ArrowUp":
      return moveVertically(popup, -1, options);
    case "ArrowLeft":
      return options.isSearching ? false : moveLeft(popup);
    case "ArrowRight":
      return options.isSearching ? false : moveRight(popup, options);
    case "Enter":
      return commit(popup, options);
  }
}
